import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useSearchParams } from "react-router-dom";
import StyledDataGrid from "../../../components/StyledDataGrid";
import {
  addDaysToDateKey,
  getChicagoDateKeyWithOffset,
} from "../../../utils/dateFilters";
import { fetchEmailTemplateSpecs, fetchEmails, sendEmail, unmatchEmailPatient } from "../../../utils/api";
import EmailDetailDrawer from "./EmailDetailDrawer";
import EmailFilters from "./EmailFilters";
import EmailPatientMatchDialog from "./EmailPatientMatchDialog";
import EmailPatientDirectory from "./EmailPatientDirectory";
import EmailPatientPicker, { patientName } from "./EmailPatientPicker";
import {
  formatDateTime,
  formatEmailAddress,
  formatLanguage,
  formatSpecCode,
  getErrorMessage,
  normalizePage,
  normalizeSpecs,
} from "./emailFormatters";
import { BASE_CHIP_SX, getStatusChipStyle } from "../../../constants/statusColors";

const FREE_FORM_OPTION = "FREE_FORM";

const FILTER_KEYS = [
  "patientId",
  "unmatchedPatient",
  "direction",
  "dateFrom",
  "dateTo",
];

const getDefaultEmailFilters = () => {
  const today = getChicagoDateKeyWithOffset(0);
  return {
    dateFrom: addDaysToDateKey(today, -30),
    dateTo: today,
  };
};

const getFiltersFromSearch = (searchParams, defaultFilters) => {
  const filters = FILTER_KEYS.reduce((acc, key) => {
    const value = searchParams.get(key);
    if (value) acc[key] = value;
    return acc;
  }, { ...defaultFilters });
  return filters;
};

const formatTableEmailAddress = (value) => {
  const email = formatEmailAddress(value);
  const replyMatch = email.match(/^reply\+([A-Z0-9]{8})[A-Z0-9-]*(@reply\..+)$/i);
  if (replyMatch) {
    return `reply+${replyMatch[1]}...${replyMatch[2]}`;
  }
  return email;
};

const freeFormBodyForPatient = (patient) => {
  const greetingName = patient?.firstName || (patient ? patientName(patient) : "Test Recipient");
  return `Dear ${greetingName},



You can reply directly to this email.

Sincerely,
TAMU IBT clinical research team
Department of Primary Care and Rural Medicine | Texas A and M Health
2900 E 29th St. Suite 100, Bryan, Texas, 77802`;
};

const renderEmailAddressCell = (params) => {
  const fullEmail = formatEmailAddress(params.value);
  const displayEmail = formatTableEmailAddress(params.value);
  return (
    <Tooltip title={fullEmail} arrow>
      <Typography
        component="span"
        variant="body2"
        sx={{
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {displayEmail}
      </Typography>
    </Tooltip>
  );
};

const renderSubjectCell = (params) => {
  const subject = params.value || "";
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {subject}
    </Typography>
  );
};

const EmailMessagesTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [matchEmail, setMatchEmail] = useState(null);
  const [unmatchEmail, setUnmatchEmail] = useState(null);
  const [unmatchSaving, setUnmatchSaving] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPatientAnchorEl, setTestPatientAnchorEl] = useState(null);
  const [testSpecs, setTestSpecs] = useState([]);
  const [testForm, setTestForm] = useState({
    recipientMode: "patient", // "patient" or "free"
    emailType: "",
    language: "en",
    recipientEmail: "",
    patient: null,
    subject: "",
    bodyText: "",
  });
  const [freeFormBodyEdited, setFreeFormBodyEdited] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testError, setTestError] = useState("");
  const [testSuccess, setTestSuccess] = useState("");
  const defaultFilters = useMemo(getDefaultEmailFilters, []);
  const today = defaultFilters.dateTo;
  const showTestEmail = true;

  const filters = useMemo(() => getFiltersFromSearch(searchParams, defaultFilters), [defaultFilters, searchParams]);

  const updateFilters = (nextFilters) => {
    const nextParams = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
      if (nextFilters[key]) nextParams.set(key, nextFilters[key]);
    });
    setSearchParams(nextParams);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  const selectedPatientScope = filters.unmatchedPatient === "true"
    ? "unmatched"
    : filters.patientId
      ? "patient"
      : "all";

  const handleSelectPatient = (patient, scope = "patient") => {
    const nextFilters = { ...filters };
    if (scope === "unmatched") {
      delete nextFilters.patientId;
      nextFilters.unmatchedPatient = "true";
    } else if (patient?.id) {
      nextFilters.patientId = patient.id;
      delete nextFilters.unmatchedPatient;
    } else {
      delete nextFilters.patientId;
      delete nextFilters.unmatchedPatient;
    }
    updateFilters(nextFilters);
  };

  const loadEmails = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchEmails({
        ...filters,
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });
      const page = normalizePage(response);
      setRows(page.content);
      setRowCount(page.totalElements);
    } catch (err) {
      console.error("Failed to load emails:", err);
      setRows([]);
      setRowCount(0);
      setError(getErrorMessage(err, "Failed to load emails."));
    } finally {
      setLoading(false);
    }
  }, [filters, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const openTestDialog = async () => {
    setTestDialogOpen(true);
    setTestError("");
    setTestSuccess("");
    setFreeFormBodyEdited(false);
    try {
      const response = await fetchEmailTemplateSpecs();
      const specs = normalizeSpecs(response);
      setTestSpecs(specs);
      const firstSpecCode = specs[0]?.specCode || "";
      setTestForm((current) => ({
        ...current,
        emailType: current.emailType || firstSpecCode || FREE_FORM_OPTION,
      }));
    } catch (err) {
      console.error("Failed to load test email specs:", err);
      setTestSpecs([]);
      setTestError(getErrorMessage(err, "Failed to load test templates."));
    }
  };

  const handleSendTestEmail = async () => {
    const recipientEmail = testForm.recipientMode === "free"
      ? testForm.recipientEmail.trim()
      : testForm.patient?.email || "";
    if (!recipientEmail) return;
    const freeForm = testForm.emailType === FREE_FORM_OPTION;
    if (freeForm && (!testForm.subject.trim() || !testForm.bodyText.trim())) return;
    if (!freeForm && !testForm.emailType) return;

    setTestSending(true);
    setTestError("");
    setTestSuccess("");
    try {
      const payload = freeForm
        ? {
            mode: FREE_FORM_OPTION,
            patientId: testForm.recipientMode === "patient" ? testForm.patient?.id : undefined,
            recipientEmail,
            language: testForm.language,
            subject: testForm.subject.trim(),
            bodyText: testForm.bodyText,
          }
        : {
            mode: "TEMPLATE",
            patientId: testForm.recipientMode === "patient" ? testForm.patient?.id : undefined,
            recipientEmail,
            specCode: testForm.emailType,
            language: testForm.language,
          };
      await sendEmail(payload);
      setTestSuccess("Email sent.");
      await loadEmails();
    } catch (err) {
      console.error("Failed to send test email:", err);
      setTestError(getErrorMessage(err, "Failed to send email."));
    } finally {
      setTestSending(false);
    }
  };

  const handleTestPatientSelect = (patient, scope) => {
    if (scope !== "patient" || !patient?.id) return;
    setTestForm((current) => ({
      ...current,
      patient,
      recipientEmail: patient.email || "",
      bodyText: current.emailType === FREE_FORM_OPTION && (!freeFormBodyEdited || !current.bodyText.trim())
        ? freeFormBodyForPatient(patient)
        : current.bodyText,
    }));
    setTestPatientAnchorEl(null);
  };

  const handleRecipientModeChange = (event) => {
    const mode = event.target.value;
    setTestForm((current) => ({
      ...current,
      recipientMode: mode,
      recipientEmail: mode === "free" ? "" : (current.patient?.email || ""),
      patient: mode === "free" ? null : current.patient,
    }));
  };

  const updateTestEmailType = (event) => {
    const emailType = event.target.value;
    setTestForm((current) => ({
      ...current,
      emailType,
      bodyText: emailType === FREE_FORM_OPTION && !freeFormBodyEdited
        ? freeFormBodyForPatient(current.patient)
        : current.bodyText,
    }));
    if (emailType === FREE_FORM_OPTION && !testForm.bodyText.trim()) {
      setFreeFormBodyEdited(false);
    }
  };

  useEffect(() => {
    if (!testDialogOpen || testForm.emailType !== FREE_FORM_OPTION || freeFormBodyEdited) {
      return;
    }
    setTestForm((current) => {
      if (current.emailType !== FREE_FORM_OPTION) return current;
      const defaultBody = freeFormBodyForPatient(current.patient);
      if (current.bodyText === defaultBody) return current;
      return { ...current, bodyText: defaultBody };
    });
  }, [freeFormBodyEdited, testDialogOpen, testForm.emailType, testForm.patient]);

  const openEmailDetail = useCallback((row) => {
    if (row.status === "FAILED") return;
    setSelectedEmail(row);
    setSelectedEmailId(row.emailId);
  }, []);

  const closeEmailDetail = () => {
    setSelectedEmail(null);
    setSelectedEmailId(null);
  };

  const handlePatientMatched = (updatedEmail, patient) => {
    const patientName = patient?.name || `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim();
    setRows((current) => current.map((row) => (
      row.emailId === updatedEmail.emailId
        ? {
            ...row,
            ...updatedEmail,
            patientName,
          }
        : row
    )));
    if (selectedEmailId === updatedEmail.emailId) {
      setSelectedEmail((current) => ({
        ...current,
        ...updatedEmail,
        patientName,
      }));
    }
  };

  const handlePatientUnmatched = async () => {
    if (!unmatchEmail?.emailId) return;
    setUnmatchSaving(true);
    setError("");
    try {
      const response = await unmatchEmailPatient(unmatchEmail.emailId);
      const updatedEmail = response?.data || response || {};
      setRows((current) => current.map((row) => (
        row.emailId === unmatchEmail.emailId
          ? {
              ...row,
              ...updatedEmail,
              patientId: "",
              patientName: "",
            }
          : row
      )));
      if (selectedEmailId === unmatchEmail.emailId) {
        setSelectedEmail((current) => ({
          ...current,
          ...updatedEmail,
          patientId: "",
          patientName: "",
        }));
      }
      setUnmatchEmail(null);
      await loadEmails();
    } catch (err) {
      console.error("Failed to unmatch email participant:", err);
      setError(getErrorMessage(err, "Failed to unmatch participant."));
    } finally {
      setUnmatchSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: "time",
      headerName: "Time",
      minWidth: 165,
      flex: 0.8,
      valueFormatter: (value) => formatDateTime(value),
    },
    { field: "patientName", headerName: "Participant", minWidth: 160, flex: 0.8 },
    {
      field: "direction",
      headerName: "Direction",
      minWidth: 115,
      flex: 0.45,
      renderCell: (params) => {
        let label = "";
        if (params.value === "OUTBOUND") label = "OUT";
        if (params.value === "INBOUND") label = "IN";
        if (!label) return null;
        return (
          <Chip
            size="small"
            label={label}
            sx={{ ...BASE_CHIP_SX, ...getStatusChipStyle(label) }}
          />
        );
      },
    },
    {
      field: "displayFollowUpCode",
      headerName: "Follow-Up",
      minWidth: 130,
      flex: 0.55,
      valueGetter: (value, row) => value || row?.displayFollowUpCode || "",
    },
    {
      field: "fromAddress",
      headerName: "From",
      minWidth: 240,
      flex: 0.95,
      renderCell: renderEmailAddressCell,
    },
    {
      field: "toAddress",
      headerName: "To",
      minWidth: 240,
      flex: 0.95,
      renderCell: renderEmailAddressCell,
    },
    {
      field: "subject",
      headerName: "Subject",
      minWidth: 520,
      flex: 2.2,
      renderCell: renderSubjectCell,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 0.5,
      renderCell: (params) => {
        let label = "";
        if (params.value === "SENT" || params.value === "RECEIVED") label = "SUCCESS";
        if (params.value === "FAILED") label = "FAILED";
        if (!label) return null;
        return (
          <Chip
            size="small"
            label={label}
            sx={{ ...BASE_CHIP_SX, ...getStatusChipStyle(label) }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 120,
      flex: 0.35,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) => {
        const failed = params.row.status === "FAILED";
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ width: "100%" }}>
            <Tooltip title={failed ? "Failed emails cannot be opened." : "View email"} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={failed}
                  onClick={() => openEmailDetail(params.row)}
                  aria-label="View email"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Match participant" arrow>
              <IconButton
                size="small"
                onClick={() => setMatchEmail(params.row)}
                aria-label="Match participant"
              >
                <PersonSearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {params.row.patientId && (
              <Tooltip title="Unmatch participant" arrow>
                <IconButton
                  size="small"
                  onClick={() => setUnmatchEmail(params.row)}
                  aria-label="Unmatch participant"
                >
                  <PersonRemoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ], [openEmailDetail]);

  const testDialogFreeForm = testForm.emailType === FREE_FORM_OPTION;
  const testPatientPickerOpen = Boolean(testPatientAnchorEl);
  const canSendDialogEmail = Boolean(
    (testForm.recipientMode === "free" ? testForm.recipientEmail.trim() : testForm.patient?.email)
      && (testDialogFreeForm
        ? testForm.subject.trim() && testForm.bodyText.trim()
        : testForm.emailType)
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ mb: 1.5 }}>
        <EmailFilters
          filters={filters}
          today={today}
          onChange={updateFilters}
          onClear={clearFilters}
          onSendTestEmail={openTestDialog}
          canSendTestEmail={showTestEmail}
          patientControl={(
            <EmailPatientDirectory
              selectedPatientId={filters.patientId || ""}
              selectedScope={selectedPatientScope}
              onSelectPatient={handleSelectPatient}
            />
          )}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0 }} data-tour-id="tour-email-messages-table">
        <StyledDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.emailId}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 3, textAlign: "center" }}>
                No emails found for the selected filters.
              </Box>
            ),
          }}
        />
      </Box>

      <EmailDetailDrawer
        emailId={selectedEmailId}
        listItem={selectedEmail}
        open={Boolean(selectedEmailId)}
        onClose={closeEmailDetail}
        onMatchPatient={(email) => setMatchEmail({ ...selectedEmail, ...email })}
      />

      <EmailPatientMatchDialog
        email={matchEmail}
        open={Boolean(matchEmail)}
        onClose={() => setMatchEmail(null)}
        onMatched={handlePatientMatched}
      />

      <Dialog open={Boolean(unmatchEmail)} onClose={() => !unmatchSaving && setUnmatchEmail(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Unmatch Participant</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Remove the participant match from this email?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnmatchEmail(null)} disabled={unmatchSaving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handlePatientUnmatched} disabled={unmatchSaving}>
            Unmatch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={testDialogOpen}
        onClose={() => {
          if (!testSending) {
            setTestPatientAnchorEl(null);
            setTestDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Email</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Recipient Type
              </Typography>
              <RadioGroup
                row
                value={testForm.recipientMode}
                onChange={handleRecipientModeChange}
              >
                <FormControlLabel
                  value="patient"
                  control={<Radio />}
                  label="Patient Account"
                />
                <FormControlLabel
                  value="free"
                  control={<Radio />}
                  label="Free Email"
                />
              </RadioGroup>
            </FormControl>

            {testForm.recipientMode === "free" ? (
              <TextField
                placeholder="Enter free email address"
                size="small"
                fullWidth
                value={testForm.recipientEmail}
                onChange={(event) => setTestForm((current) => ({ ...current, recipientEmail: event.target.value }))}
              />
            ) : (
              <>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<PersonSearchIcon fontSize="small" />}
                  onClick={(event) => setTestPatientAnchorEl(event.currentTarget)}
                  sx={{
                    height: 42,
                    justifyContent: "flex-start",
                    textTransform: "none",
                    color: testForm.patient ? "#222" : "#666",
                    borderColor: "#c7c7c7",
                    px: 1.5,
                  }}
                >
                  <Typography variant="body2" noWrap sx={{ fontWeight: testForm.patient ? 700 : 400 }}>
                    {testForm.patient ? patientName(testForm.patient) : "Select patient"}
                  </Typography>
                </Button>
                <Popover
                  open={testPatientPickerOpen}
                  anchorEl={testPatientAnchorEl}
                  onClose={() => setTestPatientAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: {
                      mt: 0.75,
                      width: testPatientAnchorEl ? testPatientAnchorEl.clientWidth : 560,
                      maxWidth: "calc(100vw - 48px)",
                      border: "1px solid #c7c7c7",
                      borderRadius: 1,
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.16)",
                    },
                  }}
                >
                  <EmailPatientPicker
                    selectedPatientId={testForm.patient?.id || ""}
                    selectedScope={testForm.patient?.id ? "patient" : "all"}
                    includeContact
                    showContact={false}
                    showPhone={false}
                    compact={true}
                    showHeader={false}
                    onSelectPatient={handleTestPatientSelect}
                    height={360}
                  />
                </Popover>
                {testForm.patient && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      border: "1px solid #e2e2e2",
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">Patient</Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                        {patientName(testForm.patient)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1.4, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" noWrap color={testForm.patient.email ? "text.primary" : "error"}>
                        {testForm.patient.email || "No email"}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => setTestForm((current) => ({
                        ...current,
                        patient: null,
                        recipientEmail: "",
                        bodyText: current.emailType === FREE_FORM_OPTION && !freeFormBodyEdited
                          ? freeFormBodyForPatient(null)
                          : current.bodyText,
                      }))}
                      sx={{ alignSelf: "center", flexShrink: 0 }}
                    >
                      Clear
                    </Button>
                  </Stack>
                )}
              </>
            )}
            <TextField
              select
              placeholder="Email Type"
              SelectProps={{ displayEmpty: true }}
              size="small"
              fullWidth
              value={testForm.emailType}
              onChange={updateTestEmailType}
            >
              {testSpecs.map((spec) => (
                <MenuItem key={spec.specCode} value={spec.specCode}>
                  {formatSpecCode(spec.specCode)}
                </MenuItem>
              ))}
              <MenuItem value={FREE_FORM_OPTION}>Free Form</MenuItem>
            </TextField>
            {!testDialogFreeForm && (
              <TextField
                select
                placeholder="Language"
                SelectProps={{ displayEmpty: true }}
                size="small"
                fullWidth
                value={testForm.language}
                onChange={(event) => setTestForm((current) => ({ ...current, language: event.target.value }))}
              >
                {["en", "es"].map((language) => (
                  <MenuItem key={language} value={language}>
                    {formatLanguage(language)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {testDialogFreeForm && (
              <>
                <TextField
                  placeholder="Subject"
                  size="small"
                  fullWidth
                  value={testForm.subject}
                  onChange={(event) => setTestForm((current) => ({ ...current, subject: event.target.value }))}
                />
                <TextField
                  placeholder="Body"
                  fullWidth
                  multiline
                  minRows={10}
                  value={testForm.bodyText}
                  onChange={(event) => {
                    setFreeFormBodyEdited(true);
                    setTestForm((current) => ({ ...current, bodyText: event.target.value }));
                  }}
                />
              </>
            )}
            {testError && <Alert severity="error">{testError}</Alert>}
            {testSuccess && <Alert severity="success">{testSuccess}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTestPatientAnchorEl(null);
              setTestDialogOpen(false);
            }}
            disabled={testSending}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSendTestEmail}
            disabled={testSending || !canSendDialogEmail}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailMessagesTab;
